// src/pages/Partner/PartnerResources.jsx

import {
  useState,
} from "react";

import {
  BookOpen,
  ChevronDown,
  Clock3,
  HelpCircle,
  Lightbulb,
  ShieldCheck,
  Users,
} from "lucide-react";


const RESOURCES = [
  {
    id: "introduce-gwin",
    title: "How to introduce Gwin",
    minutes: "6 min read",
    summary:
      "A short guide for introducing Gwin to learners clearly and setting the right expectations from the start.",
    icon: Lightbulb,
    points: [
      "Start with the problem Gwin is helping the learner solve, rather than walking through every feature at once.",
      "Explain that Gwin is a learning and practical business-finance tool. It is not a funding application, accounting system, credit score or funding guarantee.",
      "Start with one clear action and let the learner become comfortable with the platform before introducing more of the journey.",
      "Be clear that private financial information remains protected and that Partner access is limited to authorised programme information.",
    ],
  },

  {
    id: "support-without-doing",
    title:
      "How to support without completing tasks for learners",
    minutes: "5 min read",
    summary:
      "Gwin works best when learners complete their own learning and practical work. This guide helps you support without taking over.",
    icon: Users,
    points: [
      "Guide the learner through the process, but allow them to enter and complete their own work.",
      "If they are unsure where to find information, help them understand where they could look rather than deciding what they should enter.",
      "Never enter, change or invent a learner's financial information on their behalf.",
      "Focus your support on helping the learner understand the activity and complete it confidently themselves.",
    ],
  },

  {
    id: "explain-progress",
    title: "How to explain Gwin progress",
    minutes: "4 min read",
    summary:
      "A simple way to explain what the progress information in the Partner Portal means — and what it does not mean.",
    icon: HelpCircle,
    points: [
      "Learning progress shows how far a learner has moved through the learning journey available to them.",
      "Completed practical tasks show that a learner has recorded completion of practical work connected to their learning journey.",
      "Recent activity helps Partner teams understand whether learners are actively engaging with the programme.",
      "XP, levels and completion indicators reflect activity inside Gwin. They should not be treated as a measure of the financial health of the learner's business.",
    ],
  },

  {
    id: "privacy-expectations",
    title: "Privacy expectations",
    minutes: "5 min read",
    summary:
      "What Partner teams can work with, what remains private, and how to explain this to learners.",
    icon: ShieldCheck,
    points: [
      "Partner access is intended for authorised programme administration, learner support, participation, progress and completion information.",
      "Do not assume that access to a learner means access to every piece of information they have entered into Gwin.",
      "Detailed financial information, private notes and other sensitive information should only be available where the relevant consent and product functionality explicitly allow it.",
      "Partner access must never be treated as permission to edit a learner's private business or financial information.",
    ],
  },
];


export default function PartnerResources() {
  const [
    openResourceId,
    setOpenResourceId,
  ] = useState(
    RESOURCES[0]?.id || null
  );


  function toggleResource(
    resourceId
  ) {
    setOpenResourceId(
      (current) =>
        current === resourceId
          ? null
          : resourceId
    );
  }


  return (
    <>
      {/* ==================================================
          PAGE HEADER
      ================================================== */}

      <header className="rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm md:p-8">
        <div className="max-w-3xl">
          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
            Partner · Resources
          </p>

          <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] text-[#333333] md:text-[42px]">
            Resources
          </h1>

          <p className="mt-3 text-sm leading-relaxed text-[#757575] md:text-base">
            Practical guidance for
            Partner teams supporting
            learners through Gwin.
          </p>
        </div>
      </header>


      {/* ==================================================
          INTRODUCTION
      ================================================== */}

      <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm md:p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
            <BookOpen className="h-5 w-5" />
          </div>

          <div>
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Partner guide
            </p>

            <h2 className="mt-1 text-xl font-black text-[#333333]">
              Supporting learners
              well
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575]">
              These short guides
              explain how to introduce
              Gwin, support learners
              without taking over
              their work, explain
              progress clearly and
              protect learner privacy.
            </p>
          </div>
        </div>
      </section>


      {/* ==================================================
          RESOURCE LIST
      ================================================== */}

      <section className="mt-7">
        <div className="mb-4">
          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
            Guides
          </p>

          <h2 className="mt-1 text-xl font-black text-[#333333]">
            Partner resources
          </h2>
        </div>


        <div className="space-y-4">
          {RESOURCES.map(
            (resource) => (
              <ResourceCard
                key={
                  resource.id
                }
                resource={
                  resource
                }
                open={
                  openResourceId ===
                  resource.id
                }
                onToggle={() =>
                  toggleResource(
                    resource.id
                  )
                }
              />
            )
          )}
        </div>
      </section>


      {/* ==================================================
          STATIC NOTICE
      ================================================== */}

      <section className="mt-7 rounded-[24px] border border-dashed border-[#FFD2B5] bg-[#FFF9F5] p-5 md:p-6">
        <p className="text-sm font-black text-[#333333]">
          More resources will be
          added over time.
        </p>

        <p className="mt-1 max-w-3xl text-sm leading-relaxed text-[#757575]">
          This area will eventually
          contain additional Partner
          guides, programme support
          material and useful
          resources as they become
          available.
        </p>
      </section>
    </>
  );
}


function ResourceCard({
  resource,
  open,
  onToggle,
}) {
  const Icon =
    resource.icon;

  const contentId =
    `partner-resource-${resource.id}`;

  return (
    <article className="overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={
          contentId
        }
        className="group flex w-full items-start gap-4 p-5 text-left transition hover:bg-[#FFFDFC] md:p-6"
      >
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D] transition group-hover:bg-[#FFE9DA]">
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-base font-black leading-snug text-[#333333] md:text-lg">
                {resource.title}
              </h3>

              <div className="mt-1.5 flex items-center gap-1.5 text-xs font-bold text-[#999999]">
                <Clock3 className="h-3.5 w-3.5" />

                {
                  resource.minutes
                }
              </div>
            </div>

            <div
              className={[
                "flex h-9 w-9 flex-none items-center justify-center rounded-xl border border-[#EFEFEF] bg-white text-[#757575] transition",
                open
                  ? "rotate-180 border-[#FFD2B5] text-[#FF914D]"
                  : "group-hover:border-[#FFD2B5] group-hover:text-[#FF914D]",
              ].join(" ")}
            >
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>

          <p className="mt-3 max-w-4xl text-sm leading-relaxed text-[#757575]">
            {resource.summary}
          </p>
        </div>
      </button>


      {open && (
        <div
          id={contentId}
          className="border-t border-[#EFEFEF] bg-[#FFFDFC] px-5 py-5 md:px-6"
        >
          <div className="ml-0 md:ml-[60px]">
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Key guidance
            </p>

            <ul className="mt-4 space-y-3">
              {resource.points.map(
                (
                  point,
                  index
                ) => (
                  <li
                    key={`${resource.id}-${index}`}
                    className="flex gap-3"
                  >
                    <span className="mt-[7px] h-2 w-2 flex-none rounded-full bg-[#FF914D]" />

                    <p className="text-sm leading-relaxed text-[#555555]">
                      {point}
                    </p>
                  </li>
                )
              )}
            </ul>
          </div>
        </div>
      )}
    </article>
  );
}